import { Message, SessionId, HandleId, ErrorMessage } from '../message';
import { transaction } from '../../utility';

export interface Transport {
    subscribeMessage(sessionId: number, onmessage: (msg: Message) => Promise<void>): void;

    sendRequest<T>(command: string, body?: object): Promise<T>;
    sendRequest<T>(command: string, sessionId: SessionId, body?: object): Promise<T>;
    sendRequest<T>(command: string, sessionId: SessionId, handleId: HandleId, body?: object): Promise<T>;

    sendAsyncRequest(command: string, sessionId: SessionId, handleId: HandleId, body?: object): Promise<Message>;

    keepalive(sessionId: number): Promise<void>;
}

export class ErrorResponse extends Error {
    constructor(public error: ErrorMessage['error']) {
        super(`error response ${error.code}: ${error.reason}`);
    }
}

export class UnexpectedResponse extends Error {
    constructor(public response: Message) {
        super(`unexpected response: ${response.janus}`);
    }
}

export abstract class TransportBase implements Transport {
    async subscribeMessage(sessionId: number, onmessage: (msg: Message) => void) {
        this._subscribers.set(sessionId, onmessage);
    }

    sendRequest<T>(command: string, body?: object): Promise<T>;
    sendRequest<T>(command: string, sessionId: SessionId, body?: object): Promise<T>;
    sendRequest<T>(command: string, sessionId: SessionId, handleId: HandleId, body?: object): Promise<T>;
    sendRequest<T>(command: string, sessionIdOrBody?: SessionId | object, handleIdOrBody?: HandleId | object, body?: object): Promise<T> {
        if (body != undefined) {
            return this.__sendRequest<T>(command, sessionIdOrBody as SessionId, handleIdOrBody as HandleId, body);
        }
        if (handleIdOrBody != undefined) {
            if (typeof handleIdOrBody !== 'object') {
                return this.__sendRequest<T>(command, sessionIdOrBody as SessionId, handleIdOrBody as HandleId, undefined);
            } else {
                return this.__sendRequest<T>(command, sessionIdOrBody as SessionId, undefined, handleIdOrBody as Body);
            }
        }
        if (sessionIdOrBody != undefined) {
            if (typeof sessionIdOrBody !== 'object') {
                return this.__sendRequest<T>(command, sessionIdOrBody as SessionId, undefined, undefined);
            } else {
                return this.__sendRequest<T>(command, undefined, undefined, sessionIdOrBody as Body);
            }
        }
        return this.__sendRequest<T>(command, undefined, undefined, undefined);
    }

    private async __sendRequest<T>(command: string, sessionId: SessionId | undefined, handleId: HandleId | undefined, body: object | undefined) {
        const tx = transaction();
        await this._sendMessage(tx, command, sessionId, handleId, body);

        for (; ;) {
            const msg = await this._waitForResponse(tx);
            switch (msg.janus) {
                case 'success':
                    if ('data' in msg) {
                        return msg.data as T;
                    } else {
                        throw new UnexpectedResponse(msg);
                    }
                case 'ack':
                    break;
                case 'error':
                    throw new ErrorResponse(msg.error);
                default:
                    throw new UnexpectedResponse(msg);
            }
        }
    }

    async sendAsyncRequest(command: string, sessionId: SessionId, handleId: HandleId, body?: object) {
        const tx = transaction();
        await this._sendMessage(tx, command, sessionId, handleId, body);

        for (; ;) {
            const msg = await this._waitForResponse(tx);
            switch (msg.janus) {
                case 'ack':
                    break;
                case 'error':
                    throw new ErrorResponse(msg.error);
                default:
                    return msg;
            }
        }
    }

    protected abstract _startSubscribing(sessionId: number): void;
    protected abstract _sendMessage(tx: string, command: string, sessionId: SessionId | undefined, handleId: HandleId | undefined, body: object | undefined): Promise<void>;
    abstract keepalive(sessionId: number): Promise<void>;

    private readonly _waitingTransactions = new Map<string, (msg: Message) => void>();
    private readonly _subscribers = new Map<number, (msg: Message) => void>();

    protected async _waitForResponse(tx: string) {
        return new Promise<Message>(resolve => {
            this._waitingTransactions.set(tx, msg => {
                resolve(msg);
            });
        });
    }

    private _completeTransaction(transaction: string, msg: Message) {
        const fire = this._waitingTransactions.get(transaction);
        if (fire) {
            fire(msg);
            return;
        }
        console.warn(`unexpected transaction ${msg.transaction ?? '(no transaction Id)'}`, msg);
    }

    protected _onmessage(msg: Message) {
        if ('transaction' in msg && msg.transaction) {
            return this._completeTransaction(msg.transaction, msg);
        }

        if ('session_id' in msg && msg.session_id) {
            const subscriber = this._subscribers.get(msg.session_id);
            if (subscriber) {
                subscriber(msg);
            } else {
                console.log(`untransacted message to unknown session ${msg.session_id}`, msg);
            }
        } else {
            console.log('untransacted non-session message', msg);
        }
    }
}
